import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useLanguage } from '@/lib/i18n';
import { useSalonProfile } from '@/contexts/SalonProfileContext';
import { isUseMode, getAvailable, getMinThreshold, getDerivedUnitCount, getCostPerUse, isLowStock, isOutOfStock } from '@/lib/inventoryUtils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Plus, Edit, Trash2, Package, AlertTriangle, IndianRupee, ClipboardList } from 'lucide-react';

function apiProductToCamel(p) {
  if (!p) return p;
  return {
    id: p.id,
    name: p.name,
    category: p.category,
    unit: p.unit,
    stock: p.stock,
    minStock: p.min_stock,
    costPrice: p.cost_price,
    brand: p.brand,
    trackingMode: p.tracking_mode || 'unit',
    usesPerUnit: p.uses_per_unit,
    useUnit: p.use_unit,
    totalUsesAvailable: p.total_uses_available,
    usesMinThreshold: p.uses_min_threshold,
    lastRestockTotalUses: p.last_restock_total_uses,
    costPerUse: p.cost_per_use,
  };
}

function productToApiPayload(p) {
  return {
    name: p.name,
    category: p.category,
    unit: p.unit,
    stock: Number(p.stock) ?? 0,
    min_stock: Number(p.minStock) ?? 0,
    cost_price: Number(p.costPrice) ?? 0,
    brand: p.brand || '',
    tracking_mode: p.trackingMode || 'unit',
    uses_per_unit: p.usesPerUnit ?? null,
    use_unit: p.useUnit || null,
    total_uses_available: p.totalUsesAvailable ?? null,
    uses_min_threshold: p.usesMinThreshold ?? null,
    last_restock_total_uses: p.lastRestockTotalUses ?? null,
    cost_per_use: p.costPerUse ?? null,
  };
}

const CATEGORIES = ['Hair', 'Skin', 'Nails', 'Spa', 'Other'];
const UNITS = ['ml', 'grams', 'pieces', 'bottles', 'strips', 'sachets'];

const categoryColors = {
  Hair: 'bg-blue-100 text-blue-700',
  Skin: 'bg-pink-100 text-pink-700',
  Nails: 'bg-purple-100 text-purple-700',
  Spa: 'bg-green-100 text-green-700',
  Other: 'bg-gray-100 text-gray-700',
};

const ADD_REASONS = ['new_stock', 'return', 'correction'];
const REDUCE_REASONS = ['used', 'damaged', 'expired', 'correction'];
const REASON_KEYS = {
  new_stock: 'inv_reason_new_stock',
  return: 'inv_reason_return',
  correction: 'inv_reason_correction',
  used: 'inv_reason_used',
  damaged: 'inv_reason_damaged',
  expired: 'inv_reason_expired',
};

const emptyForm = { name: '', category: 'Hair', unit: 'ml', stock: '', minStock: '', costPrice: '', brand: '', trackingMode: 'unit', usesPerUnit: '', useUnit: 'stroke', usesMinThreshold: '' };

const REASON_TO_API = {
  new_stock: 'New Stock',
  return: 'Return',
  correction: 'Correction',
  used: 'Used in Service',
  damaged: 'Damaged',
  expired: 'Expired',
};

export default function Inventory() {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('All'); // All | Low Stock | Out of Stock
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [stockDialogProduct, setStockDialogProduct] = useState(null);
  const [stockMode, setStockMode] = useState('add'); // add | reduce
  const [stockQty, setStockQty] = useState('');
  const [stockReason, setStockReason] = useState('new_stock');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [stockHistory, setStockHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [clearHistoryConfirm, setClearHistoryConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const { t } = useLanguage();
  const { salonName } = useSalonProfile();

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await api.get('/inventory');
      const data = Array.isArray(res.data) ? res.data.map(apiProductToCamel) : [];
      setProducts(data);
    } catch {
      setLoadError(true);
      toast.error(t('inv_load_error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadStockHistoryList = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get('/stock-history', { params: { limit: 50 } });
      setStockHistory(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error(t('inv_history_load_error'));
    } finally {
      setHistoryLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    if (historyOpen) loadStockHistoryList();
  }, [historyOpen, loadStockHistoryList]);

  const lowStockCount = products.filter(p => isLowStock(p)).length;
  const outOfStockCount = products.filter(p => isOutOfStock(p)).length;
  const inventoryValue = products.reduce((sum, p) => {
    if (isUseMode(p)) return sum + getDerivedUnitCount(p) * (Number(p.costPrice) || 0);
    return sum + (Number(p.stock) || 0) * (Number(p.costPrice) || 0);
  }, 0);
  const showLowStockAlert = lowStockCount + outOfStockCount > 0 && !alertDismissed;

  const handleSave = async () => {
    const useTracking = form.trackingMode === 'use';
    const err = {};
    const name = (form.name || '').trim();
    if (name.length < 2) err.name = t('val_name_min');
    else if (name.length > 100) err.name = t('val_name_max');
    const stockNum = form.stock === '' ? NaN : Number(form.stock);
    if (!useTracking && (form.stock === '' || isNaN(stockNum) || stockNum < 0)) err.stock = form.stock === '' ? t('svc_req') : isNaN(stockNum) ? t('val_stock_number') : t('val_stock_min');
    const minStockNum = form.minStock === '' ? NaN : Number(form.minStock);
    if (!useTracking && form.minStock !== '' && (isNaN(minStockNum) || minStockNum < 0)) err.minStock = isNaN(minStockNum) ? t('val_stock_number') : t('val_stock_min');
    const costNum = form.costPrice === '' ? 0 : Number(form.costPrice);
    if (form.costPrice !== '' && (isNaN(costNum) || costNum < 0)) err.costPrice = isNaN(costNum) ? t('val_stock_number') : t('val_stock_min');
    if (useTracking) {
      const upu = Number(form.usesPerUnit);
      const uth = Number(form.usesMinThreshold);
      if (!(upu >= 1)) err.usesPerUnit = form.usesPerUnit === '' ? t('val_uses_required') : t('val_uses_min');
      if (!(form.useUnit || '').trim()) err.useUnit = t('val_uses_required');
      if (form.usesMinThreshold !== '' && (isNaN(uth) || uth < 0)) err.usesMinThreshold = t('val_stock_min');
      if (form.stock === '' || isNaN(stockNum) || stockNum < 0) err.stock = form.stock === '' ? t('svc_req') : isNaN(stockNum) ? t('val_stock_number') : t('val_stock_min');
    }
    if (Object.keys(err).length > 0) {
      setFieldErrors(err);
      toast.error(t('svc_req'));
      return;
    }
    setFieldErrors({});
    setSaving(true);
    const costPrice = parseFloat(form.costPrice) || 0;
    const stock = Number(form.stock) || 0;
    const minStock = Number(form.minStock) || 0;
    const product = {
      name: form.name.trim(),
      category: form.category,
      unit: form.unit,
      costPrice,
      brand: (form.brand || '').trim(),
    };
    if (useTracking) {
      const usesPerUnit = Math.max(1, Number(form.usesPerUnit) || 1);
      const useUnit = (form.useUnit || 'stroke').trim().toLowerCase() || 'stroke';
      const usesMinThreshold = Math.max(0, Number(form.usesMinThreshold) || 0);
      product.trackingMode = 'use';
      product.usesPerUnit = usesPerUnit;
      product.useUnit = useUnit;
      product.usesMinThreshold = usesMinThreshold;
      product.totalUsesAvailable = stock * usesPerUnit;
      const newTotal = stock * usesPerUnit;
      const existing = products.find(pr => pr.id === editingId);
      product.lastRestockTotalUses = editingId && existing?.lastRestockTotalUses != null ? existing.lastRestockTotalUses : newTotal;
    } else {
      product.stock = stock;
      product.minStock = minStock;
    }
    try {
      const payload = productToApiPayload(product);
      if (editingId) {
        await api.put(`/inventory/${editingId}`, payload);
      } else {
        await api.post('/inventory', payload);
      }
      await loadProducts();
      setDialogOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      toast.success(editingId ? t('svc_updated') : t('svc_added'));
    } catch {
      toast.error(t('inv_save_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/inventory/${deleteId}`);
      await loadProducts();
      setDeleteId(null);
      toast.success(t('svc_deleted'));
    } catch {
      toast.error(t('inv_delete_error'));
    }
  };

  const openEdit = (p) => {
    setFieldErrors({});
    setEditingId(p.id);
    const useMode = isUseMode(p);
    setForm({
      name: p.name,
      category: p.category,
      unit: p.unit,
      stock: useMode ? String(getDerivedUnitCount(p)) : String(p.stock ?? ''),
      minStock: useMode ? '' : String(p.minStock ?? ''),
      costPrice: String(p.costPrice || ''),
      brand: p.brand || '',
      trackingMode: useMode ? 'use' : 'unit',
      usesPerUnit: useMode ? String(p.usesPerUnit ?? '') : '',
      useUnit: useMode ? (p.useUnit || 'stroke') : 'stroke',
      usesMinThreshold: useMode ? String(p.usesMinThreshold ?? '') : '',
    });
    setDialogOpen(true);
  };

  const openStockDialog = (p, mode) => {
    setStockDialogProduct(p);
    setStockMode(mode);
    setStockQty('');
    setStockReason(mode === 'add' ? 'new_stock' : 'used');
  };

  const handleStockUpdate = async () => {
    if (!stockDialogProduct || !stockQty || Number(stockQty) <= 0) return;
    const qty = Number(stockQty);
    const p = stockDialogProduct;
    const useMode = isUseMode(p);
    const reasonApi = REASON_TO_API[stockReason] || 'Correction';
    let adjustment = qty;
    if (useMode) {
      if (stockMode === 'add') {
        const perUnit = Math.max(1, Number(p.usesPerUnit) || 1);
        adjustment = qty * perUnit;
      } else {
        adjustment = qty;
      }
    }
    try {
      const body = {
        adjustment: Math.abs(adjustment),
        type: stockMode,
        reason: reasonApi,
      };
      const res = await api.patch(`/inventory/${p.id}/stock`, body);
      const updated = apiProductToCamel(res.data);
      setProducts(prev => prev.map(pr => pr.id === p.id ? updated : pr));
      setStockDialogProduct(null);
      toast.success(t('inv_stock_updated'));
      const newAvail = getAvailable(updated);
      const minTh = getMinThreshold(updated);
      if (newAvail <= minTh && newAvail > 0) {
        toast.warning(t('low_stock_toast').replace('{product}', p.name));
      } else if (newAvail === 0) {
        toast.warning(t('low_stock_toast').replace('{product}', p.name));
      }
      if (historyOpen) loadStockHistoryList();
    } catch {
      toast.error(t('inv_stock_update_error'));
    }
  };

  const handleWhatsAppAlert = () => {
    const low = products.filter(p => isLowStock(p) || isOutOfStock(p));
    if (low.length === 0) return;
    const name = salonName || 'Salon';
    let msg = `🏪 *${name}* — ${t('inv_low_stock_alert')}\n\n`;
    msg += t('inv_whatsapp_restock_intro') + '\n\n';
    low.forEach(p => {
      if (isUseMode(p)) {
        msg += `• ${p.name}: ${getAvailable(p)} ${p.useUnit}s (Min: ${getMinThreshold(p)} ${p.useUnit}s)\n`;
      } else {
        msg += `• ${p.name}: ${p.stock} ${p.unit} (Min: ${p.minStock} ${p.unit})\n`;
      }
    });
    msg += '\n_' + t('inv_whatsapp_footer') + '_';
    window.open('https://wa.me/?text=' + encodeURIComponent(msg));
  };

  const handleClearHistory = async () => {
    try {
      await api.delete('/stock-history', { params: { confirm: true } });
      setStockHistory([]);
      setClearHistoryConfirm(false);
      setHistoryOpen(false);
      toast.success(t('inv_clear_history'));
    } catch {
      toast.error(t('inv_history_clear_error'));
    }
  };

  const filtered = products.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
    const low = isLowStock(p);
    const out = isOutOfStock(p);
    let matchFilter = true;
    if (filter === 'Low Stock') matchFilter = low;
    else if (filter === 'Out of Stock') matchFilter = out;
    return matchSearch && matchFilter;
  });

  const getStockColor = (available, minThreshold) => {
    if (available === 0) return 'text-red-600 font-bold';
    if (minThreshold > 0 && available <= minThreshold) return 'text-amber-600 font-bold';
    return 'text-green-600 font-bold';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-32 rounded-2xl" />)}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-48 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12 bg-amber-50 border border-amber-200 rounded-2xl">
          <p className="text-amber-800 font-medium mb-4">{t('inv_load_error')}</p>
          <Button onClick={() => loadProducts()} className="rounded-xl bg-[#f0a500] hover:bg-[#d69400] text-[#1a1a2e] font-semibold">
            {t('inv_retry')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Top bar: Add button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative w-full sm:flex-1 sm:max-w-md min-w-0">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            data-testid="inventory-search"
            placeholder={t('inv_product_search')}
            className="h-12 sm:h-14 pl-12 text-base sm:text-lg rounded-xl w-full"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:shrink-0">
          <Button
            variant="outline"
            onClick={() => setHistoryOpen(true)}
            className="h-12 sm:h-14 min-h-[44px] w-full sm:w-auto sm:flex-none px-4 sm:px-5 rounded-xl gap-2 text-sm sm:text-base border-2 whitespace-normal sm:whitespace-nowrap"
          >
            <ClipboardList className="w-5 h-5 shrink-0" />
            {t('inv_stock_history')}
          </Button>
          <Button
            data-testid="add-product-btn"
            onClick={() => { setEditingId(null); setForm(emptyForm); setFieldErrors({}); setDialogOpen(true); }}
            className="h-12 sm:h-14 min-h-[44px] w-full sm:w-auto sm:flex-none px-4 sm:px-6 rounded-xl bg-[#f0a500] hover:bg-[#d69400] text-[#1a1a2e] text-base sm:text-lg font-semibold gap-2 whitespace-normal sm:whitespace-nowrap"
          >
            <Plus className="w-5 h-5 shrink-0" />
            {t('inv_product_add')}
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="bg-[#1a1a2e] text-white p-3 rounded-xl">
                <Package className="w-6 h-6" />
              </div>
            </div>
            <p className="text-sm text-gray-500 font-medium mb-1">{t('inv_total_products')}</p>
            <p className="font-poppins text-2xl font-bold text-[#1a1a2e]">{products.length}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="bg-amber-500 text-white p-3 rounded-xl">
                <AlertTriangle className="w-6 h-6" />
              </div>
            </div>
            <p className="text-sm text-gray-500 font-medium mb-1">{t('inv_low_stock')}</p>
            <p className="font-poppins text-2xl font-bold text-[#1a1a2e]">{lowStockCount + outOfStockCount}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="bg-[#f0a500] text-[#1a1a2e] p-3 rounded-xl">
                <IndianRupee className="w-6 h-6" />
              </div>
            </div>
            <p className="text-sm text-gray-500 font-medium mb-1">{t('inv_inventory_value')}</p>
            <p className="font-poppins text-2xl font-bold text-[#1a1a2e]">₹{inventoryValue.toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Low stock alert banner */}
      {showLowStockAlert && (
        <div className="flex items-center justify-between gap-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <span className="text-amber-800 font-medium">
            ⚠️ {lowStockCount + outOfStockCount} {t('inv_products_low_stock')} —
          </span>
          <div className="flex gap-2 flex-shrink-0">
            <Button size="sm" onClick={handleWhatsAppAlert} className="bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-lg">
              {t('inv_whatsapp_alert')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setAlertDismissed(true)} className="rounded-lg">
              {t('inv_dismiss')}
            </Button>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-white rounded-xl p-1 h-auto flex flex-wrap gap-1">
          <TabsTrigger value="All" data-testid="inv-filter-all" className="rounded-lg px-4 py-2.5 text-base data-[state=active]:bg-[#1a1a2e] data-[state=active]:text-white">
            {t('inv_filter_all')}
          </TabsTrigger>
          <TabsTrigger value="Low Stock" data-testid="inv-filter-low" className="rounded-lg px-4 py-2.5 text-base data-[state=active]:bg-[#1a1a2e] data-[state=active]:text-white">
            {t('inv_filter_low_stock')}
          </TabsTrigger>
          <TabsTrigger value="Out of Stock" data-testid="inv-filter-out" className="rounded-lg px-4 py-2.5 text-base data-[state=active]:bg-[#1a1a2e] data-[state=active]:text-white">
            {t('inv_filter_out_of_stock')}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Product grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.length === 0 ? (
          <div className="col-span-full text-center text-gray-400 py-12 text-lg">{t('inv_none')}</div>
        ) : filtered.map(p => {
          const useMode = isUseMode(p);
          const avail = getAvailable(p);
          const minTh = getMinThreshold(p);
          const low = isLowStock(p);
          const out = isOutOfStock(p);
          if (useMode) {
            const derived = getDerivedUnitCount(p);
            const costPerUse = getCostPerUse(p);
            const useUnitLabel = (p.useUnit || 'stroke').toLowerCase();
            const useUnitPlural = useUnitLabel + (useUnitLabel.endsWith('s') ? '' : 's');
            const origTotal = Math.max(avail, Number(p.lastRestockTotalUses) || avail);
            const pct = origTotal > 0 ? Math.min(100, (avail / origTotal) * 100) : 0;
            return (
              <Card
                key={p.id}
                className={`border-0 shadow-sm rounded-2xl hover:shadow-md transition-all ${low || out ? 'border-l-4 border-l-amber-500 bg-amber-50/30' : ''}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <Badge className={categoryColors[p.category] || categoryColors.Other}>{p.category}</Badge>
                    <div className="flex gap-1 items-center">
                      {(low || out) && <span className="text-amber-600 text-xs font-medium">⚠️ {t('inv_low_stock')}</span>}
                      <Button variant="ghost" size="icon" className="w-8 h-8 rounded-lg" onClick={() => openEdit(p)}>
                        <Edit className="w-4 h-4 text-gray-400" />
                      </Button>
                      <Button variant="ghost" size="icon" className="w-8 h-8 rounded-lg" onClick={() => setDeleteId(p.id)}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                  <h3 className="font-semibold text-lg text-[#1a1a2e] mb-2">{p.name}</h3>
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                    <div
                      className={`h-2 rounded-full transition-all ${pct > 50 ? 'bg-green-500' : pct > 20 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-600 mb-1">
                    📦 {derived.toFixed(2)} {p.unit} ({avail} {useUnitPlural} left)
                  </p>
                  <p className="text-xs text-gray-500 mb-2">{t('use_alert_below')}: {minTh} {useUnitPlural}</p>
                  <p className="text-sm text-gray-500 mb-1">
                    {t('inv_cost_price')} {t('use_per_use')}: ₹{costPerUse.toFixed(2)}
                  </p>
                  <p className="text-sm text-gray-500 mb-3">{t('inv_cost_price')} per {p.unit}: ₹{Number(p.costPrice)}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-lg border-[#f0a500] text-[#f0a500] hover:bg-[#f0a500]/10 gap-1.5"
                  onClick={() => openStockDialog(p, 'add')}
                >
                  <Plus className="w-4 h-4 flex-shrink-0" /> {t('inv_add_stock')}
                </Button>
              </CardContent>
            </Card>
            );
          }
          return (
            <Card
              key={p.id}
              className={`border-0 shadow-sm rounded-2xl hover:shadow-md transition-all ${low ? 'border-l-4 border-l-amber-500 bg-amber-50/30' : ''}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <Badge className={categoryColors[p.category] || categoryColors.Other}>{p.category}</Badge>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="w-8 h-8 rounded-lg" onClick={() => openEdit(p)}>
                      <Edit className="w-4 h-4 text-gray-400" />
                    </Button>
                    <Button variant="ghost" size="icon" className="w-8 h-8 rounded-lg" onClick={() => setDeleteId(p.id)}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </div>
                <h3 className="font-semibold text-lg text-[#1a1a2e] mb-2">{p.name}</h3>
                <p className="text-sm text-gray-500 mb-2">{p.unit}</p>
                <p className="mb-2">
                  <span className="text-sm text-gray-500">{t('inv_current_stock')}: </span>
                  <span className={getStockColor(p.stock, p.minStock)}>{p.stock} {p.unit}</span>
                </p>
                <p className="text-sm text-gray-500 mb-1">Min: {p.minStock} {p.unit}</p>
                <p className="text-sm text-gray-500 mb-3">{t('inv_cost_price')}: ₹{p.costPrice} per {p.unit}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-lg border-[#f0a500] text-[#f0a500] hover:bg-[#f0a500]/10 gap-1.5"
                  onClick={() => openStockDialog(p, 'add')}
                >
                  <Plus className="w-4 h-4 flex-shrink-0" /> {t('inv_add_stock')}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Add/Edit Product Modal */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#faf8f5] rounded-t-3xl md:rounded-3xl max-w-lg w-[calc(100vw-2rem)] md:w-[calc(100%-2rem)] max-h-[95vh] md:max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="flex-shrink-0 p-4 sm:p-6 pb-4 border-b border-gray-200/60">
            <DialogTitle className="font-poppins text-lg sm:text-xl">{editingId ? t('edit') : t('inv_product_add')}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 pt-4 space-y-4">
            <div>
              <Label className="text-base mb-1.5 block">{t('inv_product_name')} *</Label>
              <Input className="h-11 sm:h-12 rounded-xl" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              {fieldErrors.name && <p className="text-red-500 text-sm mt-1">{fieldErrors.name}</p>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-base mb-1.5 block">{t('inv_category')}</Label>
                <select className="w-full h-11 sm:h-12 rounded-xl border border-gray-200 px-3 text-base bg-white" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-base mb-1.5 block">{t('inv_unit')}</Label>
                <select className="w-full h-11 sm:h-12 rounded-xl border border-gray-200 px-3 text-base bg-white" value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}>
                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-4">
              <div>
                <Label className="text-base font-medium">{t('use_tracking')}</Label>
                <p className="text-xs text-gray-500 mt-0.5">{t('use_modal_helper')}</p>
              </div>
              <Switch
                checked={form.trackingMode === 'use'}
                onCheckedChange={v => setForm(f => ({ ...f, trackingMode: v ? 'use' : 'unit', usesPerUnit: v ? f.usesPerUnit : '', useUnit: v ? (f.useUnit || 'stroke') : 'stroke', usesMinThreshold: v ? f.usesMinThreshold : '' }))}
              />
            </div>
            {form.trackingMode === 'use' && (
              <>
                <div>
                  <Label className="text-base mb-1.5 block">{t('use_per_unit').replace('{unit}', form.unit)}</Label>
                  <Input type="number" min={1} placeholder="e.g. 80" className="h-12 rounded-xl" value={form.usesPerUnit} onChange={e => setForm(f => ({ ...f, usesPerUnit: e.target.value }))} />
                  {fieldErrors.usesPerUnit && <p className="text-red-500 text-sm mt-1">{fieldErrors.usesPerUnit}</p>}
                </div>
                <div>
                  <Label className="text-base mb-1.5 block">{t('use_one_call')}</Label>
                  <Input placeholder="stroke / pump / spray / scoop" className="h-12 rounded-xl" value={form.useUnit} onChange={e => setForm(f => ({ ...f, useUnit: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-base mb-1.5 block">{t('use_alert_below')}</Label>
                  <Input type="number" min={0} placeholder="e.g. 20" className="h-12 rounded-xl" value={form.usesMinThreshold} onChange={e => setForm(f => ({ ...f, usesMinThreshold: e.target.value }))} />
                  <p className="text-xs text-gray-500 mt-1">{t('use_alert_helper')}</p>
                </div>
                {editingId && (() => {
                  const existing = products.find(pr => pr.id === editingId);
                  if (existing && isUseMode(existing)) {
                    const x = getAvailable(existing);
                    const y = getDerivedUnitCount(existing);
                    const uu = (existing.useUnit || 'stroke').toLowerCase();
                    return (
                      <p className="text-sm text-gray-600 rounded-lg bg-gray-100 p-2">
                        {t('use_status').replace('{x}', String(x)).replace('{useUnit}', uu).replace('{y}', y.toFixed(2)).replace('{unit}', existing.unit)}
                      </p>
                    );
                  }
                  return null;
                })()}
              </>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-base mb-1.5 block">{form.trackingMode === 'use' ? `${t('inv_current_stock')} (${form.unit}) *` : `${t('inv_current_stock')} *`}</Label>
                <Input type="number" min={0} className="h-11 sm:h-12 rounded-xl" value={form.stock} onChange={e => setForm(f => ({ ...f, stock: e.target.value }))} />
                {fieldErrors.stock && <p className="text-red-500 text-sm mt-1">{fieldErrors.stock}</p>}
              </div>
              {form.trackingMode !== 'use' && (
                <div>
                  <Label className="text-base mb-1.5 block">{t('inv_min_threshold')} *</Label>
                  <Input type="number" min={0} className="h-11 sm:h-12 rounded-xl" value={form.minStock} onChange={e => setForm(f => ({ ...f, minStock: e.target.value }))} />
                  <p className="text-xs text-gray-500 mt-1">{t('inv_min_alert_helper')}</p>
                  {fieldErrors.minStock && <p className="text-red-500 text-sm mt-1">{fieldErrors.minStock}</p>}
                </div>
              )}
            </div>
            <div>
              <Label className="text-base mb-1.5 block">{t('inv_cost_price')} (₹)</Label>
              <Input type="number" step="0.01" min={0} className="h-12 rounded-xl" value={form.costPrice} onChange={e => setForm(f => ({ ...f, costPrice: e.target.value }))} />
              {fieldErrors.costPrice && <p className="text-red-500 text-sm mt-1">{fieldErrors.costPrice}</p>}
            </div>
            <div>
              <Label className="text-base mb-1.5 block">{t('inv_brand')} / {t('notes')}</Label>
              <Input className="h-12 rounded-xl" value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} placeholder={t('notes')} />
            </div>
          </div>
          <div className="flex-shrink-0 flex flex-col-reverse sm:flex-row gap-3 justify-end p-4 sm:p-6 pt-4 border-t border-gray-200/60">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="h-11 sm:h-12 px-6 rounded-xl text-base w-full sm:w-auto">{t('cancel')}</Button>
            <Button onClick={handleSave} disabled={saving} className="h-11 sm:h-12 px-8 rounded-xl bg-[#f0a500] hover:bg-[#d69400] text-[#1a1a2e] text-base font-semibold w-full sm:w-auto">
              {saving ? t('saving') : t('inv_save_karo')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stock adjustment dialog */}
      <Dialog open={!!stockDialogProduct} onOpenChange={() => setStockDialogProduct(null)}>
        <DialogContent className="bg-[#faf8f5] rounded-t-3xl md:rounded-3xl max-w-md w-[calc(100vw-2rem)] md:w-[calc(100%-2rem)] max-h-[95vh] md:max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="flex-shrink-0 p-4 sm:p-6 pb-4 border-b border-gray-200/60">
            <DialogTitle className="font-poppins text-xl">{stockDialogProduct?.name}</DialogTitle>
          </DialogHeader>
          {stockDialogProduct && (() => {
            const useMode = isUseMode(stockDialogProduct);
            const avail = getAvailable(stockDialogProduct);
            const derived = getDerivedUnitCount(stockDialogProduct);
            const useUnitLabel = (stockDialogProduct.useUnit || 'stroke').toLowerCase();
            const useUnitPlural = useUnitLabel + (useUnitLabel.endsWith('s') ? '' : 's');
            const unitLabel = stockDialogProduct.unit;
            return (
            <>
              <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 pt-4 space-y-4">
                <p className="text-gray-600">
                  {t('inv_current_stock')}:{' '}
                  <strong>
                    {useMode ? `${derived.toFixed(2)} ${unitLabel} (${avail} ${useUnitPlural})` : `${stockDialogProduct.stock} ${unitLabel}`}
                  </strong>
                </p>
                <Tabs value={stockMode} onValueChange={v => { setStockMode(v); setStockReason(v === 'add' ? 'new_stock' : 'used'); }}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="add" className="rounded-lg">{t('inv_stock_add')}</TabsTrigger>
                    <TabsTrigger value="reduce" className="rounded-lg">{t('inv_stock_reduce')}</TabsTrigger>
                  </TabsList>
                </Tabs>
                <div>
                  <Label className="text-base mb-1.5 block">
                    {useMode
                      ? (stockMode === 'add' ? t('use_kitni_units').replace('{units}', unitLabel) : t('use_kitne_use').replace('{useUnit}', useUnitPlural))
                      : t('inv_quantity')}
                  </Label>
                  <Input type="number" min={1} className="h-12 rounded-xl" value={stockQty} onChange={e => setStockQty(e.target.value)} placeholder={useMode && stockMode === 'add' ? 'e.g. 2' : useMode && stockMode === 'reduce' ? 'e.g. 40' : undefined} />
                </div>
                <div>
                  <Label className="text-base mb-1.5 block">{t('inv_reason')}</Label>
                  <select className="w-full h-12 rounded-xl border border-gray-200 px-3 text-base bg-white" value={stockReason} onChange={e => setStockReason(e.target.value)}>
                    {(stockMode === 'add' ? ADD_REASONS : REDUCE_REASONS).map(r => (
                      <option key={r} value={r}>{t(REASON_KEYS[r])}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex-shrink-0 p-4 sm:p-6 pt-4 border-t border-gray-200/60">
                <Button
                  onClick={handleStockUpdate}
                  className="w-full h-11 sm:h-12 rounded-xl bg-[#f0a500] hover:bg-[#d69400] text-[#1a1a2e] font-semibold"
                >
                  {t('inv_update_karo')}
                </Button>
              </div>
            </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Stock History Sheet */}
      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent side="right" className="w-full sm:max-w-xl bg-[#faf8f5] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-poppins text-xl">{t('inv_stock_history')}</SheetTitle>
          </SheetHeader>
          <div className="mt-6">
            {historyLoading ? (
              <div className="space-y-2">{[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-10 w-full rounded-lg" />)}</div>
            ) : stockHistory.length === 0 ? (
              <p className="text-gray-400 text-center py-8">{t('inv_none')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 font-medium text-gray-600">{t('rpt_date')}</th>
                      <th className="text-left py-2 font-medium text-gray-600">{t('inv_time')}</th>
                      <th className="text-left py-2 font-medium text-gray-600">{t('inv_product')}</th>
                      <th className="text-left py-2 font-medium text-gray-600">{t('inv_type')}</th>
                      <th className="text-left py-2 font-medium text-gray-600">{t('inv_quantity')}</th>
                      <th className="text-left py-2 font-medium text-gray-600">{t('inv_reason')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockHistory.map((h, i) => (
                      <tr key={h.id || i} className="border-b border-gray-100">
                        <td className="py-2">{h.date}</td>
                        <td className="py-2">{h.time}</td>
                        <td className="py-2">{h.product_name}</td>
                        <td className="py-2">
                          <Badge className={h.type === 'add' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {h.type === 'add' ? '+' : '-'}
                          </Badge>
                        </td>
                        <td className="py-2">
                          {h.tracking_mode === 'use' && h.uses_changed != null
                            ? (h.type === 'add' ? `${h.quantity} units = ${h.uses_changed} ${(h.use_unit || 'stroke')}s` : `${h.uses_changed} ${(h.use_unit || 'stroke')}s`)
                            : h.quantity}
                        </td>
                        <td className="py-2">{h.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {stockHistory.length > 0 && !historyLoading && (
              <div className="mt-6">
                <Button variant="outline" size="sm" onClick={() => setClearHistoryConfirm(true)} className="rounded-lg">
                  {t('inv_clear_history')}
                </Button>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Clear history confirmation */}
      <AlertDialog open={clearHistoryConfirm} onOpenChange={setClearHistoryConfirm}>
        <AlertDialogContent className="max-h-[90vh] md:max-h-[85vh] overflow-y-auto rounded-t-3xl md:rounded-2xl w-[calc(100vw-2rem)] max-w-md p-4 sm:p-6">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('del_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('del_confirm_desc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{t('del_cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearHistory} className="bg-red-500 hover:bg-red-600 rounded-xl">{t('del_confirm_btn')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="max-h-[90vh] md:max-h-[85vh] overflow-y-auto rounded-t-3xl md:rounded-2xl w-[calc(100vw-2rem)] max-w-md p-4 sm:p-6">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-poppins">{t('del_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('del_confirm_desc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{t('del_cancel')}</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-product-btn" onClick={handleDelete} className="bg-red-500 hover:bg-red-600 rounded-xl">{t('del_confirm_btn')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
